import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Loader2, LayoutList } from 'lucide-react';

interface CollectionBlockProps {
  typeSlug?: string;
  title?: string;
  maxItems?: number;
}

interface CollectionEntry {
  id: string;
  title: string;
  slug?: string | null;
  dataJson: Record<string, unknown>;
  publishedAt: string | null;
}

export function CollectionBlock({ props }: { props: CollectionBlockProps }) {
  const { typeSlug, title, maxItems = 10 } = props;
  const [entries, setEntries] = useState<CollectionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!typeSlug) {
      setLoading(false);
      return;
    }
    api
      .get<{ entries: CollectionEntry[] }>(
        `/customer/portal/collections/${encodeURIComponent(typeSlug)}?limit=${maxItems}`,
      )
      .then((res) => setEntries(res.data.entries ?? []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [typeSlug, maxItems]);

  if (!typeSlug) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <LayoutList size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-400">No collection configured.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error || entries.length === 0) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <LayoutList size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-400">
          {error ? 'Failed to load content.' : 'No entries available.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {title && <h2 className="text-xl font-semibold text-zinc-900">{title}</h2>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map((entry) => {
          const data = entry.dataJson ?? {};
          // Try to extract a cover image from common field names
          const imageUrl =
            (data.imageUrl as string | undefined) ??
            (data.image as string | undefined) ??
            (data.cover as string | undefined);
          // Try to extract a summary/excerpt
          const excerpt =
            (data.excerpt as string | undefined) ??
            (data.summary as string | undefined) ??
            (data.description as string | undefined);
          const entryPath = `/c/${encodeURIComponent(typeSlug)}/${encodeURIComponent(entry.slug ?? entry.id)}`;

          return (
            <Link
              key={entry.id}
              to={entryPath}
              className="block bg-white border border-zinc-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {imageUrl && (
                <div className="aspect-video overflow-hidden bg-zinc-100">
                  <img src={imageUrl} alt={entry.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4 space-y-1.5">
                <h3 className="font-semibold text-zinc-900 text-sm leading-snug line-clamp-2">
                  {entry.title}
                </h3>
                {excerpt && <p className="text-xs text-zinc-500 line-clamp-3">{excerpt}</p>}
                {entry.publishedAt && (
                  <p className="text-[10px] text-zinc-400">
                    {new Date(entry.publishedAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
